/**
 * Zustand store for Grossman-Miller Simulator state management
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  ModelParams,
  SimulationState,
  TimeStep,
  DEFAULT_PARAMS,
  SimulationOptions,
} from '../model/types';
import { runSimulation } from '../model/grossmanMillerEngine';
import { generateRandomSeed } from '../model/rng';

interface SimulationStore {
  // Current parameters
  params: ModelParams;

  // Simulation state (null if not run yet)
  simulation: SimulationState | null;

  // UI state
  showAdvanced: boolean;
  showFormulas: boolean;

  // Actions
  setParam: <K extends keyof ModelParams>(key: K, value: ModelParams[K]) => void;
  setParams: (partial: Partial<ModelParams>) => void;
  resetParamsToDefault: () => void;

  // Simulation control
  runNewSimulation: (options?: SimulationOptions) => void;
  newRandomPath: () => void;
  runDeterministicSimulation: () => void;

  // Time navigation
  stepForward: () => void;
  stepBackward: () => void;
  jumpToTime: (t: TimeStep) => void;

  // UI toggles
  toggleAdvanced: () => void;
  toggleFormulas: () => void;

  // URL/persistence
  loadFromUrl: (queryString: string) => void;
  serializeToUrl: () => string;
}

export const useSimulationStore = create<SimulationStore>()(
  persist(
    (set, get) => ({
      params: { ...DEFAULT_PARAMS },
      simulation: null,
      showAdvanced: false,
      showFormulas: false,

      setParam: (key, value) => {
        set((state) => ({
          params: { ...state.params, [key]: value },
        }));
      },

      setParams: (partial) => {
        set((state) => ({
          params: { ...state.params, ...partial },
        }));
      },

      resetParamsToDefault: () => {
        set({ params: { ...DEFAULT_PARAMS } });
      },

      runNewSimulation: (options) => {
        const { params } = get();
        try {
          const simulation = runSimulation(params, options);
          set({ simulation });
        } catch (error) {
          console.error('Simulation error:', error);
        }
      },

      newRandomPath: () => {
        const { params } = get();
        try {
          const simulation = runSimulation(params, {
            seed: generateRandomSeed(),
          });
          set({ simulation });
        } catch (error) {
          console.error('Simulation error:', error);
        }
      },

      runDeterministicSimulation: () => {
        const { params } = get();
        try {
          const simulation = runSimulation(params, {
            deterministicPath: true,
            fixedEpsilon2: 0,
            fixedEpsilon3: 0,
          });
          set({ simulation });
        } catch (error) {
          console.error('Simulation error:', error);
        }
      },

      stepForward: () => {
        set((state) => {
          if (!state.simulation || state.simulation.currentTime >= 3) {
            return state;
          }
          return {
            simulation: {
              ...state.simulation,
              currentTime: (state.simulation.currentTime + 1) as TimeStep,
            },
          };
        });
      },

      stepBackward: () => {
        set((state) => {
          if (!state.simulation || state.simulation.currentTime <= 1) {
            return state;
          }
          return {
            simulation: {
              ...state.simulation,
              currentTime: (state.simulation.currentTime - 1) as TimeStep,
            },
          };
        });
      },

      jumpToTime: (t) => {
        set((state) => {
          if (!state.simulation || t < 1 || t > 3) {
            return state;
          }
          return {
            simulation: {
              ...state.simulation,
              currentTime: t,
            },
          };
        });
      },

      toggleAdvanced: () => {
        set((state) => ({ showAdvanced: !state.showAdvanced }));
      },

      toggleFormulas: () => {
        set((state) => ({ showFormulas: !state.showFormulas }));
      },

      loadFromUrl: (queryString) => {
        const urlParams = new URLSearchParams(queryString);
        const partial: Partial<ModelParams> = {};

        if (urlParams.has('n')) partial.n = parseInt(urlParams.get('n')!, 10);
        if (urlParams.has('i')) partial.i = parseFloat(urlParams.get('i')!);
        if (urlParams.has('mu')) partial.mu = parseFloat(urlParams.get('mu')!);
        if (urlParams.has('sigma')) partial.sigma = parseFloat(urlParams.get('sigma')!);
        if (urlParams.has('a')) partial.a = parseFloat(urlParams.get('a')!);
        if (urlParams.has('W0')) partial.W0 = parseFloat(urlParams.get('W0')!);
        if (urlParams.has('eta')) partial.eta = parseFloat(urlParams.get('eta')!);
        if (urlParams.has('c')) partial.c = parseFloat(urlParams.get('c')!);

        set({ params: { ...DEFAULT_PARAMS, ...partial } });
      },

      serializeToUrl: () => {
        const { params } = get();
        const searchParams = new URLSearchParams();

        Object.entries(params).forEach(([key, value]) => {
          searchParams.set(key, String(value));
        });

        return searchParams.toString();
      },
    }),
    {
      name: 'gm-simulator-store',
      partialize: (state) => ({ params: state.params }),
    }
  )
);
